// src/pages/contact/Contact.styled.tsx
import styled from "styled-components";
import { keyframes } from "styled-components";

export const ContactContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1.5rem;
`;

export const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  color: #2a2a2a;
  text-align: center;
  position: relative;

  &:after {
    content: "";
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: linear-gradient(90deg, #6c63ff, #5046e4);
    border-radius: 2px;
  }
`;


export const ContactLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3rem;

  @media (min-width: 992px) {
    flex-direction: row;
  }
`;

export const ContactFormContainer = styled.div`
  flex: 1.5;
  background-color: white;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.05);
  padding: 2rem;

  @media (min-width: 768px) {
    padding: 2.5rem;
  }
`;

export const ContactInfoContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

export const InfoCard = styled.div`
  background-color: white;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.05);
  padding: 2rem;
`;

export const InfoTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 1.5rem;
  position: relative;
  padding-left: 1rem;
  border-left: 4px solid #6c63ff;
`;

export const InfoList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

export const InfoItem = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const InfoIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background-color: #f0f0ff;
  border-radius: 50%;
  margin-right: 1rem;
  color: #6c63ff;
  font-size: 1.2rem;
`;

export const InfoContent = styled.div`
  flex: 1;
`;

export const InfoLabel = styled.span`
  display: block;
  font-size: 0.85rem;
  color: #777;
  margin-bottom: 0.25rem;
`;

export const InfoValue = styled.span`
  display: block;
  font-size: 1rem;
  color: #333;
  font-weight: 500;
`;

export const SocialMediaContainer = styled.div`
  margin-top: 2rem;
`;

export const SocialMediaList = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

export const SocialMediaLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 45px;
  height: 45px;
  border-radius: 50%;
  background-color: #f4f4f4;
  color: #444;
  font-size: 1.2rem;
  transition: all 0.2s ease;

  &:hover {
    background-color: #6c63ff;
    color: white;
    transform: translateY(-5px);
  }
`;

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

export const FormLabel = styled.label`
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
  color: #555;
  font-weight: 500;
`;

export const Input = styled.input<{ hasError?: boolean }>`
  padding: 0.75rem 1rem;
  border: 1px solid ${props => (props.hasError ? "#e53935" : "#e0e0e0")};
  border-radius: 8px;
  font-size: 1rem;
  transition: border 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${props => (props.hasError ? "#e53935" : "#6C63FF")};
    box-shadow: 0 0 0 2px
      ${props =>
        props.hasError ? "rgba(229, 57, 53, 0.1)" : "rgba(108, 99, 255, 0.1)"};
  }
`;

export const Textarea = styled.textarea<{ hasError?: boolean }>`
  padding: 0.75rem 1rem;
  border: 1px solid ${props => (props.hasError ? "#e53935" : "#e0e0e0")};
  border-radius: 8px;
  font-size: 1rem;
  min-height: 150px;
  resize: vertical;
  font-family: inherit;
  transition: border 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${props => (props.hasError ? "#e53935" : "#6C63FF")};
    box-shadow: 0 0 0 2px
      ${props =>
        props.hasError ? "rgba(229, 57, 53, 0.1)" : "rgba(108, 99, 255, 0.1)"};
  }
`;

export const ErrorMessage = styled.div`
  color: #e53935;
  font-size: 0.8rem;
  margin-top: 0.5rem;
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

export const SubmitButton = styled.button<{ isSubmitting: boolean }>`
  padding: 0.75rem 1rem;
  background: ${props =>
    props.isSubmitting
      ? "#9490d8"
      : "linear-gradient(90deg, #6C63FF, #5046e4)"};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: ${props => (props.isSubmitting ? "not-allowed" : "pointer")};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &:hover {
    background: ${props =>
      props.isSubmitting
        ? "#9490d8"
        : "linear-gradient(90deg, #5e56e7, #433acb)"};
    transform: ${props => (props.isSubmitting ? "none" : "translateY(-2px)")};
  }

  &:before {
    content: "";
    display: ${props => (props.isSubmitting ? "block" : "none")};
    width: 18px;
    height: 18px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top: 2px solid white;
    border-radius: 50%;
    animation: ${spin} 1s linear infinite;
  }
`;

export const SuccessMessage = styled.div`
  background-color: #e8f5e9;
  color: #2e7d32;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  i {
    font-size: 1.2rem;
  }
`;

export const ErrorAlert = styled.div`
  background-color: #ffebee;
  color: #c62828;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  i {
    font-size: 1.2rem;
  }
`;

/* Introduction and MapContainer removed as no longer used */
